import { SupportTrip } from "domain/entities/support-trip";
import { MessageTypeEnum } from "domain/enum/messageType";
import { SupportTripStatusEnum } from "domain/enum/supportTripStatus";
import { RoleInputFilter } from "domain/models/role-input-filter";
import { SupportTripInputFilter } from "domain/models/support-trip-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import TripApprovalForm from "./components/TripApprovalForm";
import { useRoleByFilter } from "presentation/hooks/use-query-role";
import { useSupportTripByFilter, useUpdateSupportTripMutation } from "presentation/hooks/use-query-support-trip";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useFilterAdminSupportTripStore } from "presentation/stores/use-filter-admin-support-trip-store";
import RegisterTripForm from "presentation/mobile/components/RegisterTripForm";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { getApiErrorMessage } from "presentation/mobile/utils/api-error.util";
import { EMAIL } from "domain/enum/email";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import usePermissionCheck from "presentation/hooks/use-check-permissions";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileFilterTabs from "presentation/mobile/components/MobileFilterTabs";
import { useStatusFilter } from "presentation/mobile/hooks/useStatusFilter";
import { SUPPORT_TRIP_STATUS_FILTERS } from "presentation/mobile/utils/status-filters.util";
import MobileActionButton from "presentation/mobile/components/MobileActionButton";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";
import { formatCurrency, formatDatePT } from "presentation/mobile/utils/format.util";
import MobileToast from "presentation/mobile/components/MobileToast";
import MobileLoadingOverlay from "presentation/mobile/components/MobileLoadingOverlay";
import MobileRefreshingIndicator from "presentation/mobile/components/MobileRefreshingIndicator";
import MobileRefreshingList from "presentation/mobile/components/MobileRefreshingList";
import { SUPPORT_TRIP_STATUS_TONES, getStatusStyle } from "presentation/mobile/utils/status-styles.util";

const FUNCTION = "trips";
const REQUIRE_FUNCTION = "tripsheets";

const possibleApprovalStatus = ["A Aprovar", "Cancelamento Pendente"];

const getTripStatus = (trip: SupportTrip) => trip.status;

interface TripItemProps {
    trip: SupportTrip;
    isExpanded: boolean;
    hasAdminRolePermission: boolean;
    currentUserId?: string;
    onToggle: () => void;
    onCancel: (trip: SupportTrip) => void;
    onApprove: (trip: SupportTrip) => void;
}

const TripItem: React.FC<TripItemProps> = ({
    trip,
    isExpanded,
    hasAdminRolePermission,
    currentUserId,
    onToggle,
    onCancel,
    onApprove,
}) => {
    const { isPressing, handlers } = useLongPress({
        onLongPress: onToggle,
    });

    const getStatusLabel = (): string => {
        if (trip.status === SupportTripStatusEnum.APROVADO && trip.rejectionReason) return "Aprovado *";
        if (trip.status === SupportTripStatusEnum.CANCELAMENTO_PENDENTE) return "Canc. Pendente";
        return trip.status ?? "-";
    };

    const statusInfo = {
        label: getStatusLabel(),
        ...getStatusStyle(SUPPORT_TRIP_STATUS_TONES, trip.status),
    };


    const isOwnTrip = !trip.employee?.id || trip.employee.id === currentUserId;
    const canCancel =
        isOwnTrip &&
        (trip.status === SupportTripStatusEnum.A_APROVAR ||
            (trip.status === SupportTripStatusEnum.APROVADO && !trip.rejectionReason));

    const canApprove =
        hasAdminRolePermission && possibleApprovalStatus.includes(trip.status);

    return (
        <div
            {...handlers}
            className="border-round-xl p-3 flex flex-column gap-2 cursor-pointer"
            style={{
                backgroundColor: "#FFFFFF",
                border: isExpanded ? "1.5px solid #0084D1" : "1px solid #E2E8F0",
                boxShadow: isExpanded ? "0 4px 14px rgba(0, 132, 209, 0.08)" : "none",
                transform: isPressing ? "scale(0.99)" : "scale(1)",
                transition: "all 0.2s ease",
            }}
        >
            {/* Linha 1: Origem → Destino + Badge + Botão '+' / '✕' */}
            <div className="w-full flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-1 overflow-hidden pr-2">
                    {isExpanded && (
                        <span
                            style={{
                                color: "#0084D1",
                                fontSize: "1.1rem",
                                lineHeight: "1",
                                marginRight: "2px",
                            }}
                        >
                            •
                        </span>
                    )}
                    <span className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {`${trip.locationOrigin || "-"} → ${trip.locationDestination || "-"}`}
                    </span>
                </div>

                <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                        className="badge-squircle"
                        style={{
                            color: statusInfo.color,
                            backgroundColor: statusInfo.bg,
                            border: `1px solid ${statusInfo.border}`,
                        }}
                    >
                        {statusInfo.label}
                    </span>

                    {/* Micro-interação: '+' gira 45º para '✕' */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        className="border-none bg-transparent cursor-pointer flex align-items-center justify-content-center p-0"
                        style={{
                            width: "24px",
                            height: "24px",
                            color: "#94A3B8",
                            outline: "none",
                        }}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                    >
                        <i
                            className="pi pi-plus text-xs"
                            style={{
                                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Linha 2: Data da Viagem e Valor */}
            <div className="w-full flex justify-content-between align-items-center">
                <span className="text-xs" style={{ color: "#64748B" }}>
                    Data: <strong style={{ color: "#0F172A" }}>{formatDateToPT(trip.dateTrip)}</strong>
                </span>
                <span className="font-bold text-base" style={{ color: "#0084D1" }}>
                    {formatCurrency(trip.tripValue)}
                </span>
            </div>

            {/* Linha 3: Distância em km e Colaborador (Sem maiúsculas) */}
            <div className="w-full flex align-items-center gap-1">
                <i className="pi pi-car text-xs" style={{ color: "#94A3B8" }} />
                <span className="text-xs font-medium" style={{ color: "#64748B" }}>
                    {trip.distanceKm ?? 0} km {trip.employee?.name ? `• ${trip.employee.name}` : ""}
                </span>
            </div>

            {/* Secção Expandida: Motivo de Rejeição e Botões Reutilizáveis */}
            {isExpanded && (
                <div
                    className="w-full flex flex-column gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px solid #F1F5F9" }}
                >
                    {/* Motivo de recusa exibido apenas se houver */}
                    {trip.rejectionReason && trip.status === SupportTripStatusEnum.NAO_APROVADO && (
                        <div
                            className="w-full p-2 border-round-md"
                            style={{ backgroundColor: "#FEF2F2", border: "1px solid #FEE2E2" }}
                        >
                            <span className="text-xs font-semibold text-red-700">
                                Motivo da não aprovação:{" "}
                            </span>
                            <span className="text-xs text-red-600">
                                {trip.rejectionReason}
                            </span>
                        </div>
                    )}

                    {trip.rejectionReason && trip.status === SupportTripStatusEnum.APROVADO && (
                        <div
                            className="w-full p-2 border-round-md flex align-items-start gap-2"
                            style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}
                        >
                            <i className="pi pi-info-circle text-xs mt-1" style={{ color: "#0084D1" }} />
                            <span className="text-xs" style={{ color: "#075985" }}>
                                O colaborador solicitou o cancelamento desta viagem mas foi rejeitado.{" "}
                                <strong>Motivo da rejeição:</strong> {trip.rejectionReason}
                            </span>
                        </div>
                    )}

                    {/* Botões Reutilizáveis alinhados à direita */}

                    {(canCancel || canApprove) && (
                        <div className="w-full flex justify-content-end gap-2 pt-1">
                            {canCancel && (
                                <MobileActionButton
                                    label="Cancelar"
                                    icon="pi pi-trash"
                                    severity="danger"
                                    onClick={() => onCancel(trip)}
                                />
                            )}
                            {canApprove && (
                                <MobileActionButton
                                    label="Aprovar"
                                    icon="pi pi-check"
                                    severity="secondary"
                                    onClick={() => onApprove(trip)}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Trips = () => {
    const { isLoading: isCheckingPermissions } = usePermissionCheck({
        requiredFunction: REQUIRE_FUNCTION,
    });
    const toast = useRef<Toast>(null);
    const adminRolesAllowed = ["Admin", "CEO", "Finanças"];
    const tripRolesAllowed = ["Colaborador com Viagens de Suporte", "Colaborador com Viagens Livres"];


    const navigationState = useNavigationStore();
    const { filters: filterAdminSupportTrip } = useFilterAdminSupportTripStore();
    const { userPermissions, userEmployee, email: userEmployeeEmail } = useAuthStore();

    const { data: rolesData } = useRoleByFilter({
        limit: 100,
    } as RoleInputFilter);

    const [trips, setTrips] = useState<SupportTrip[]>([]);
    const [expandedTripId, setExpandedTripId] = useState<string | null>(null);

    const [showRegisterModal, setShowRegisterModal] = useState<boolean>(false);
    const [tripToApprove, setTripToApprove] = useState<SupportTrip>();
    const [showApproveModal, setShowApprovalModal] = useState<boolean>(false);

    const hasAdminRolePermission = useMemo(() => {
        if (!rolesData?.data || !userPermissions) {
            return false;
        }
        const allowedRoleIds = rolesData.data
            .filter((role) => adminRolesAllowed.includes(role.name))
            .map((role) => role.id);

        return userPermissions.some((permission) =>
            allowedRoleIds.includes(permission.roleId)
        );
    }, [rolesData, userPermissions]);

    const isFree = useMemo(() => {
        if (!rolesData?.data || !userPermissions) return false;

        const freeRoleIds = rolesData.data
            .filter((role) => role.name === "Colaborador com Viagens Livres")
            .map((role) => role.id);

        return userPermissions.some((p) => freeRoleIds.includes(p.roleId));
    }, [rolesData, userPermissions]);

    const hasTripRolePermission = useMemo(() => {
        if (!rolesData?.data || !userPermissions) return false;

        const tripRoleIds = rolesData.data
            .filter((role) => tripRolesAllowed.includes(role.name))
            .map((role) => role.id);

        return userPermissions.some((p) => tripRoleIds.includes(p.roleId));
    }, [rolesData, userPermissions]);

    const { mutate: updateSupportTrip, isPending: isCancelling } = useUpdateSupportTripMutation();

    const [lazySupportParams] = useState({
        sortBy: "dateTrip",
        sortOrder: "desc",
        limit: 50,
        page: 1,
        filters: {},
    });

    const {
        data: supportTripsData,
        isError,
        isRefetching,
        refetch,
    } = useSupportTripByFilter({
        month: filterAdminSupportTrip?.month ?? undefined,
        year: filterAdminSupportTrip?.year ?? undefined,
        status: filterAdminSupportTrip?.status ?? undefined,
        employeeId: hasAdminRolePermission ? filterAdminSupportTrip?.employeeId ?? undefined : userEmployee?.id,
        sortBy: lazySupportParams.sortBy,
        sortOrder: lazySupportParams.sortOrder,
        page: lazySupportParams.page,
        limit: lazySupportParams.limit,
        ...lazySupportParams.filters,
    } as SupportTripInputFilter);

    useEffect(() => {
        if (!supportTripsData?.data) return;
        setTrips(supportTripsData.data);
    }, [supportTripsData?.data]);

    useEffect(() => {
        if (navigationState.currentRoute !== FUNCTION) {
            navigationState.setCurrentRoute(FUNCTION);
        }
    }, [navigationState]);

    const {
        activeTab,
        setActiveTab,
        filterTabs,
        filteredItems: filteredTrips,
    } = useStatusFilter(trips, SUPPORT_TRIP_STATUS_FILTERS, getTripStatus);

    const handleCloseModalAction = async () => {
        setShowApprovalModal(false);
        setTripToApprove(undefined);
        await refetch();
    };

    const handleCloseModal = () => {
        setShowApprovalModal(false);
        setTripToApprove(undefined);
    };

    const handleCancel = (trip: SupportTrip) => {
        const isPendingApproval = trip.status === SupportTripStatusEnum.A_APROVAR;

        const tripToUpdate: SupportTrip = {
            ...trip,
            status: isPendingApproval
                ? SupportTripStatusEnum.CANCELADO
                : SupportTripStatusEnum.CANCELAMENTO_PENDENTE,
            lastModifiedBy: userEmployeeEmail || EMAIL,
        };

        updateSupportTrip(tripToUpdate, {
            onSuccess: () =>
                showMessage(
                    MessageTypeEnum.SUCCESS,
                    "Sucesso",
                    isPendingApproval
                        ? "Viagem cancelada com sucesso!"
                        : "Pedido de cancelamento efetuado com sucesso!"
                ),
            onError: (error) =>
                showMessage(
                    MessageTypeEnum.ERROR,
                    "Erro",
                    getApiErrorMessage(error, "Não foi possível cancelar a viagem.")
                ),
        });
    };


    const confirmAction = (message: string, acceptCallback: () => void) => {
        confirmDialog({
            message,
            header: "Confirmação",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sim",
            rejectLabel: "Não",
            accept: acceptCallback,
        });
    };

    const showMessage = (
        type: MessageTypeEnum,
        subject: string,
        message: string
    ) => {
        toast.current?.show({
            severity: type,
            summary: subject,
            detail: message,
            life: 2500,
        });
    };

    if (!supportTripsData || isCheckingPermissions) {
        return (
            <div className="flex justify-content-center align-items-center h-screen">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex flex-column pt-2 px-3 gap-2 pb-8 align-items-center">
            <MobileToast ref={toast} />
            <MobileLoadingOverlay visible={isCancelling} message="A cancelar viagem..." />

            {/* ========== ABAS DE FILTRO REUTILIZÁVEIS ========== */}
            <MobileFilterTabs
                tabs={filterTabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                scrollable
            />

            {/* ========== TÍTULO DA SECÇÃO (SEM UPPERCASE) ========== */}
            <div className="w-full flex justify-content-between align-items-center pt-1 px-1">
                <span
                    className="font-semibold text-xs"
                    style={{ color: "#64748B" }}
                >
                    {hasAdminRolePermission ? "Todas as viagens" : "As minhas viagens"}

                </span>
                {isRefetching ? (
                    <MobileRefreshingIndicator />
                ) : (
                    <span className="text-xs" style={{ color: "#94A3B8" }}>
                        {filteredTrips.length} {filteredTrips.length === 1 ? "registo" : "registos"}
                    </span>
                )}
            </div>

            {/* ========== LISTA DE CARTÕES COM EXPANSÃO ========== */}
            <MobileRefreshingList
                isRefreshing={isRefetching}
                className="w-full flex flex-column justify-content-center gap-3"
            >
                {isError ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-exclamation-circle text-4xl text-red-500" />
                        <span className="text-sm text-center text-600">
                            Não foi possível carregar as viagens.
                        </span>
                    </div>
                ) : filteredTrips.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-map text-4xl" style={{ color: "#CBD5E1" }} />
                        <span className="text-sm text-center" style={{ color: "#64748B" }}>
                            {activeTab !== "all"
                                ? "Nenhuma viagem encontrada com este filtro."
                                : hasAdminRolePermission
                                    ? "Não existem viagens registadas."
                                    : "Não tem viagens registadas."}
                        </span>
                    </div>
                ) : (
                    filteredTrips.map((trip) => (
                        <TripItem
                            key={trip.id}
                            trip={trip}
                            isExpanded={expandedTripId === trip.id}
                            hasAdminRolePermission={hasAdminRolePermission}
                            currentUserId={userEmployee?.id}
                            onToggle={() =>
                                setExpandedTripId((prev) =>
                                    prev === trip.id ? null : trip.id ?? null
                                )
                            }
                            onCancel={(t) =>
                                confirmAction("Deseja cancelar esta viagem?", () =>
                                    handleCancel(t)
                                )
                            }
                            onApprove={(t) => {
                                setTripToApprove(t);
                                setShowApprovalModal(true);
                            }}
                        />
                    ))
                )}
            </MobileRefreshingList>

            {/* Botão Flutuante Circular de Adicionar (+) */}
            {hasTripRolePermission && !hasAdminRolePermission && (
                <Button
                    icon="pi pi-plus"
                    rounded
                    className="fixed"
                    onClick={() => setShowRegisterModal(true)}
                    style={{
                        bottom: "6rem",
                        right: "20px",
                        zIndex: 1000,
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#0084D1",
                        border: "none",
                    }}
                />
            )}

            {/* Modal de Detalhes / Aprovação */}
            <MobileBottomSheet
                visible={showApproveModal}
                onHide={handleCloseModal}
                title="Detalhes da Viagem"
                headerIcon="pi pi-check-circle"
            >
                {tripToApprove && (
                    <TripApprovalForm
                        supportTrip={tripToApprove}
                        onClose={handleCloseModalAction}
                        showMessage={showMessage}
                    />
                )}
            </MobileBottomSheet>

            {/* Modal de Registar Viagem */}
            <MobileBottomSheet
                visible={showRegisterModal}
                onHide={() => setShowRegisterModal(false)}
                title="Registar Viagem"
                headerIcon="pi pi-map"
            >
                <RegisterTripForm
                    onClose={() => {
                        setShowRegisterModal(false);
                        refetch();
                    }}
                    showMessage={showMessage}
                    isFree={isFree}
                />
            </MobileBottomSheet>

            <ConfirmDialog />
        </div>
    );
};

export default Trips;